import { CycleStage } from '../../api';

/**
 * The words the back-office's first screen uses.
 *
 * A copy module like [`reviewQueue`](./reviewQueue.ts) and
 * [`submissionCycle`](./submissionCycle.ts), and here for a reason those two
 * make sharper rather than weaker: this page shows the *same* numbers those
 * screens show, so every label it invents is a chance for the overview and the
 * screen it points at to disagree about what a word means. Where a label
 * already exists it is imported rather than rewritten -- the stage names come
 * straight from `submissionCycle`, so "Enviado" here and "Enviado" on the
 * submissions screen cannot drift apart.
 *
 * What this module does add is the framing: the three questions the page asks
 * in the order an analyst asks them, and the sentence each panel shows when the
 * answer is "nothing", because "nothing to do" is the answer this page most
 * wants to be able to give and a blank panel does not give it.
 */

export const OVERVIEW_COPY = {
  title: 'Visão geral',
  /** Completed with the cycle month, so the page states which month it is about. */
  description: (month: string) =>
    `O ciclo de ${month}: onde está o fechamento de cada contrato, o que espera análise e o que ` +
    'chegou por e-mail sem ninguém tratar.',

  // --- 1. Where each contract's month stands -------------------------------
  standingTitle: 'Fechamento do mês',
  standingDescription:
    'Um contrato por linha, com até onde o fechamento deste ciclo chegou. Abra o contrato nos ' +
    'envios para gerar o arquivo ou registrar o que a operadora respondeu.',
  standingLoading: 'Carregando o fechamento do mês…',
  standingError: 'Não foi possível carregar o fechamento do mês',
  noContractsTitle: 'Nenhum contrato',
  noContracts: 'Nenhum contrato no seu escopo. O ciclo do primeiro aparece aqui assim que existir.',
  outstanding: 'A enviar',
  overdue: 'Em atraso',
  contracts: 'Contratos',
  openInSubmissions: 'Abrir nos envios',

  // --- 2. What is waiting to be read ---------------------------------------
  workloadTitle: 'Fila de análise',
  workloadDescription:
    'O que espera análise em cada contrato neste ciclo, e quanto disso tem vigência dentro de um ' +
    'mês que a operadora já recebeu.',
  workloadLoading: 'Carregando a fila de análise…',
  workloadError: 'Não foi possível carregar a fila de análise',
  noQueueTitle: 'Nada esperando análise',
  noQueue: 'Nenhum contrato tem movimentação esperando análise neste ciclo.',
  inReview: 'Em análise',
  retroactive: 'Fora do período',
  /**
   * Said in full under the table, and not only as a column heading.
   *
   * A retroactive movement is the one thing on this page that does not wait
   * politely: the month it belongs to has already gone to the operadora, so
   * nothing this product does will file it, and an analyst who scrolls past the
   * number has missed the only item that needs a decision outside the system.
   */
  retroactiveFootnote: (count: string, contracts: string) =>
    `${count} movimentação(ões) em ${contracts} contrato(s) têm vigência dentro de um mês já ` +
    'enviado. Não entram neste ciclo e não há caminho de correção por aqui.',
  noRetroactive: 'Nenhuma movimentação com vigência dentro de um mês já enviado.',
  openReviewQueue: 'Abrir a fila de análise',

  // --- 3. What arrived by e-mail and stalled -------------------------------
  mailboxTitle: 'Caixa de entrada',
  mailboxDescription:
    'As mensagens de clientes em migração que pararam em alguém: sem remetente autorizado, ou ' +
    'sem nada aproveitável dentro.',
  mailboxLoading: 'Consultando a caixa de entrada…',
  mailboxError: 'Não foi possível consultar a caixa de entrada',
  /** The number, said as a sentence, because a bare digit is not an instruction. */
  mailboxWaiting: (count: string) =>
    `${count} mensagem(ns) aguardando alguém: cada uma precisa de um remetente autorizado ou de ` +
    'um descarte registrado.',
  /**
   * The empty case gets a sentence of its own rather than an empty table,
   * because on most days this is the answer and "nada aguardando" is worth
   * reading as a result instead of as a gap on the page.
   */
  mailboxClear: 'Nada aguardando. Toda mensagem lida já foi tratada.',
  openMailbox: 'Abrir a caixa de entrada',
} as const;

/**
 * The tone each stage gets on the standing table.
 *
 * The same rule the submissions screen applies, restated as a function because
 * both screens badge the same value and a second `if` chain written by hand is
 * how `sent` ends up green on one page and amber on the other.
 */
export function stageTone(stage: CycleStage): string {
  if (stage === CycleStage.SENT) {
    return 'badge--ok';
  }
  return stage === CycleStage.FAILED ? 'badge--blocking' : 'badge--warning';
}
